/**
 * Het gedragsdocument (sessie 50, pakket C): de vier lagen waaruit de
 * prompts van Nova en Sanne worden samengesteld, alleen lezen.
 *
 * Volgorde ligt vast: kern, kanaal, vraagtype, leerregel. Ontbreekt een
 * laag in het antwoord, dan staat hij er toch, leeg, zodat je ziet dat
 * er niets in zit in plaats van dat hij stil verdwijnt.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { vertaalApiFout } from "@/lib/api";
import {
  werkplekKennisApi,
  type GedragLaag,
  type GedragLaagSoort,
  type GedragRegel,
} from "@/lib/werkplek-api";

const LAAGVOLGORDE: GedragLaagSoort[] = [
  "kern",
  "kanaal",
  "vraagtype",
  "leerregel",
];

const LAAGTITEL: Record<GedragLaagSoort, string> = {
  kern: "Kern",
  kanaal: "Per kanaal",
  vraagtype: "Per vraagtype",
  leerregel: "Leerregels",
};

const KANAALFILTERS = [
  { sleutel: "alle", naam: "Alle kanalen" },
  { sleutel: "chat", naam: "Chat" },
  { sleutel: "whatsapp", naam: "WhatsApp" },
  { sleutel: "mail", naam: "Mail" },
  { sleutel: "telefoon", naam: "Telefoon" },
] as const;

type KanaalFilter = (typeof KANAALFILTERS)[number]["sleutel"];

const KANAALNAAM: Record<string, string> = {
  chat: "Chat",
  whatsapp: "WhatsApp",
  mail: "Mail",
  telefoon: "Telefoon",
};

const KANAALCHIP: Record<string, string> = {
  chat: "wp-chip wp-chip-chat",
  whatsapp: "wp-chip wp-chip-whatsapp",
  mail: "wp-chip wp-chip-mail",
  telefoon: "wp-chip wp-chip-telefoon",
};

const LEEG_LEERREGEL =
  "Nog geen regels. Goedgekeurde leerregels komen hier.";

/** "2026-09-24" wordt "24-09-2026"; zonder Date, dus geen tijdzone. */
function datumNl(datum: string): string {
  const delen = /^(\d{4})-(\d{2})-(\d{2})/.exec(datum);
  return delen ? `${delen[3]}-${delen[2]}-${delen[1]}` : datum;
}

export function GedragsdocumentTab() {
  const [filter, setFilter] = useState<KanaalFilter>("alle");
  const gedrag = useQuery({
    queryKey: ["werkplek", "kennis", "gedragsdocument"],
    queryFn: werkplekKennisApi.gedragsdocument,
  });

  return (
    <div className="wp-kn-gd">
      <p className="wp-kn-uitleg wp-kn-gd-uitleg">
        Bewerken komt later; dit is de enige bron voor de antwoorden van Nova
        en Sanne.
      </p>

      <div
        className="wp-kn-filter"
        role="group"
        aria-label="Filter op kanaal"
      >
        {KANAALFILTERS.map((optie) => (
          <button
            key={optie.sleutel}
            type="button"
            className="wp-knop"
            aria-pressed={filter === optie.sleutel}
            onClick={() => setFilter(optie.sleutel)}
          >
            {optie.naam}
          </button>
        ))}
      </div>

      {gedrag.isLoading ? (
        <p className="wp-kn-stil" role="status">
          Gedragsdocument laden…
        </p>
      ) : gedrag.isError || !gedrag.data ? (
        <p className="wp-kn-fout" role="alert">
          Het gedragsdocument is niet op te halen:{" "}
          {vertaalApiFout(gedrag.error, "geen antwoord van de server.")}
        </p>
      ) : (
        LAAGVOLGORDE.map((soort) => (
          <LaagBlok
            key={soort}
            soort={soort}
            laag={gedrag.data.lagen.find((laag) => laag.laag === soort)}
            filter={filter}
          />
        ))
      )}
    </div>
  );
}

function LaagBlok({
  soort,
  laag,
  filter,
}: {
  soort: GedragLaagSoort;
  laag: GedragLaag | undefined;
  filter: KanaalFilter;
}) {
  const alle = laag?.regels ?? [];
  // Alleen de kanaal-laag filtert; kern, vraagtype en leerregel blijven.
  const gefilterd =
    soort === "kanaal" && filter !== "alle"
      ? alle.filter((regel) => regel.kanaal === filter)
      : alle;
  const titel = laag?.titel || LAAGTITEL[soort];
  const telling =
    gefilterd.length === alle.length
      ? `${alle.length} ${alle.length === 1 ? "regel" : "regels"}`
      : `${gefilterd.length} van ${alle.length} regels`;

  return (
    <section
      className="wp-blok wp-kn-laag"
      data-laag={soort}
      aria-label={titel}
    >
      <h4>
        <span>{titel}</span>
        <span className="wp-koprechts wp-kn-laag-telling">{telling}</span>
      </h4>
      {gefilterd.length === 0 ? (
        <p className="wp-kn-stil wp-kn-laag-leeg">
          {alle.length > 0
            ? "Geen regels voor dit kanaal."
            : soort === "leerregel"
              ? LEEG_LEERREGEL
              : "Nog geen regels in deze laag."}
        </p>
      ) : (
        <ul className="wp-kn-gd-regels">
          {gefilterd.map((regel) => (
            <RegelRij key={regel.id} regel={regel} soort={soort} />
          ))}
        </ul>
      )}
    </section>
  );
}

function RegelRij({
  regel,
  soort,
}: {
  regel: GedragRegel;
  soort: GedragLaagSoort;
}) {
  return (
    <li className="wp-kn-gd-regel" data-regel-id={regel.id}>
      <div className="wp-kn-gd-kop">
        <b className="wp-kn-gd-onderwerp">{regel.onderwerp}</b>
        {soort === "kanaal" && regel.kanaal ? (
          <span className={KANAALCHIP[regel.kanaal] ?? "wp-chip wp-chip-grijs"}>
            {KANAALNAAM[regel.kanaal] ?? regel.kanaal}
          </span>
        ) : null}
        {soort === "vraagtype" && regel.vraagtype ? (
          <span className="wp-chip wp-chip-grijs">{regel.vraagtype}</span>
        ) : null}
      </div>
      <p className="wp-kn-gd-tekst">{regel.tekst}</p>
      <p className="wp-kn-gd-meta">
        {datumNl(regel.datum)} · {regel.bron}
      </p>
    </li>
  );
}
